import React, { useState, useMemo, useEffect, Suspense, lazy } from "react";
import { Header } from "./components/Header.js";
import { Sidebar, NavTab } from "./components/Sidebar.js";
import { CategoryBar } from "./components/CategoryBar.js";
import { VideoGrid } from "./components/VideoGrid.js";
import { AuthModal } from "./components/AuthModal.js";
import { ShieldCheck, Flame, Tv2, Settings, RefreshCw, X, Download, Compass } from "lucide-react";
import { VideoItem, SEED_VIDEOS } from "./types/video.js";
import { Playlist } from "./types/playlist.js";
import { useVideos } from "./hooks/useVideos.js";
import { AdminRoute } from "./components/AdminRoute.js";

// Lazy-loaded route components for high-performance bundle splitting
const StudioLayout = lazy(() => import("./components/StudioLayout.js").then((m) => ({ default: m.StudioLayout })));
const ChannelPage = lazy(() => import("./pages/ChannelPage.js").then((m) => ({ default: m.ChannelPage })));
const WatchView = lazy(() => import("./components/WatchView.js").then((m) => ({ default: m.WatchView })));
const OfflineLibrary = lazy(() => import("./components/OfflineLibrary.js").then((m) => ({ default: m.OfflineLibrary })));
const HistoryPage = lazy(() => import("./pages/HistoryPage.js").then((m) => ({ default: m.HistoryPage })));
const Trending = lazy(() => import("./pages/Trending.js").then((m) => ({ default: m.Trending })));
const ArchitectureView = lazy(() => import("./components/ArchitectureView.js").then((m) => ({ default: m.ArchitectureView })));
const SettingsPage = lazy(() => import("./pages/Settings.js").then((m) => ({ default: m.SettingsPage })));
const SearchResults = lazy(() => import("./pages/SearchResults.js").then((m) => ({ default: m.SearchResults })));
const VideoUploadModal = lazy(() => import("./components/VideoUploadModal.js").then((m) => ({ default: m.VideoUploadModal })));
const EditVideoModal = lazy(() => import("./components/EditVideoModal.js").then((m) => ({ default: m.EditVideoModal })));
const CreateChannelModal = lazy(() => import("./components/CreateChannelModal.js").then((m) => ({ default: m.CreateChannelModal })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.js").then((m) => ({ default: m.AdminDashboard })));
const Shorts = lazy(() => import("./pages/Shorts.js").then((m) => ({ default: m.Shorts })));
const ClipView = lazy(() => import("./pages/ClipView.js").then((m) => ({ default: m.ClipView })));
const LiveStudio = lazy(() => import("./pages/studio/LiveStudio.js").then((m) => ({ default: m.LiveStudio })));
const GuestJoinView = lazy(() => import("./pages/live/GuestJoinView.js").then((m) => ({ default: m.GuestJoinView })));

const ViewLoadingFallback: React.FC = () => (
  <div className="flex flex-col items-center justify-center min-h-[360px] w-full p-8 text-center">
    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
    <span className="text-xs text-zinc-400 font-mono">Loading module...</span>
  </div>
);
import {
  deleteVideo,
  fetchVideoById,
  LOVE_TONIGHT_VIDEO,
  SWEET_MARGARET_VIDEO,
  MARGARET_MY_LOVE_VIDEO,
} from "./services/videoService.js";
import { useI18n } from "./i18n/I18nContext.js";
import { useAuth } from "./context/AuthContext.js";
import { MiniPlayer } from "./components/player/MiniPlayer.js";
import { usePlayerSafe } from "./context/PlayerContext.js";
import { PwaInstallBanner } from "./components/common/PwaInstallBanner.js";
import { NetworkBanner } from "./components/common/NetworkBanner.js";
import { ErrorBoundary } from "./components/common/ErrorBoundary.js";
import { useMaintenanceGate } from "./hooks/useMaintenanceGate.js";
import { UnderConstruction } from "./pages/UnderConstruction.js";


export const App: React.FC = () => {
  const { t } = useI18n();
  const { isLocked, setIsLocked } = useMaintenanceGate();
  const { isAuthModalOpen, setIsAuthModalOpen, isCreateChannelModalOpen, setIsCreateChannelModalOpen, channel } = useAuth();

  // Dynamic Video Data Fetching from Fastify / Cloudflare Stream
  const { videos, isLoading, refetch } = useVideos();

  // Navigation & Layout State
  const [activeTab, setActiveTab] = useState<NavTab>("home");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const handleToggleSidebar = () => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setIsMobileMenuOpen((prev) => !prev);
    } else {
      setIsSidebarCollapsed((prev) => !prev);
    }
  };

  // Search & Selected Video & Creator Channel State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState<VideoItem>(SEED_VIDEOS[0]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | undefined>(undefined);
  const [selectedCreator, setSelectedCreator] = useState<string>("helixcreator");
  const [selectedClipId, setSelectedClipId] = useState<string>("clip_jovial_acoustic_refrain");
  const [guestToken, setGuestToken] = useState<string>("");

  // Video Management / Edit Modal State
  const [editingVideo, setEditingVideo] = useState<VideoItem | null>(null);

  // Global Player Context for Persistent Floating Miniplayer
  const playerCtx = usePlayerSafe();

  useEffect(() => {
    if (!playerCtx) return;
    playerCtx.onExpandRef.current = (video) => {
      setSelectedVideo(video);
      setActiveTab("player");
      const targetUid = video.streamMediaId || video.uid || video.id;
      if (typeof window !== "undefined" && window.history?.pushState) {
        window.history.pushState(null, "", `/watch/${targetUid}`);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
  }, [playerCtx]);

  // Sync selected video when initial video collection finishes loading
  useEffect(() => {
    const isWatchRoute =
      typeof window !== "undefined" &&
      (window.location.pathname.startsWith("/watch/") ||
        window.location.hash.startsWith("#watch/") ||
        window.location.search.includes("v="));
    if (!isWatchRoute && videos && videos.length > 0 && selectedVideo.id === SEED_VIDEOS[0].id) {
      setSelectedVideo(videos[0]);
    }
  }, [videos]);

  // Handle direct hash / URL path routing for channels or watch view + URL sanitizer
  useEffect(() => {
    const handleUrlRouting = () => {
      let path = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash;

      // Cleanly rewrite /index.html to /
      if (path === "/index.html" || path.endsWith("/index.html")) {
        const cleanPath = path.replace(/\/index\.html$/, "") || "/";
        try {
          window.history.replaceState(null, "", cleanPath + search + hash);
        } catch {}
        path = cleanPath;
      }

      if (path === "/" && !hash && !search) {
        setActiveTab("home");
      } else if (
        path === "/shorts" ||
        hash === "#shorts" ||
        path.startsWith("/shorts") ||
        hash.startsWith("#shorts") ||
        path === "/reels" ||
        hash === "#reels"
      ) {
        setActiveTab("shorts");
      } else if (path === "/trending" || hash === "#trending") {
        setActiveTab("trending");
      } else if (path === "/history" || hash === "#history") {
        setActiveTab("history");
      } else if (path === "/subscriptions" || hash === "#subscriptions") {
        setActiveTab("subscriptions");
      } else if (path === "/offline" || hash === "#offline") {
        setActiveTab("offline");
      } else if (path === "/settings" || hash === "#settings") {
        setActiveTab("settings");
      } else if (path === "/admin" || hash === "#admin") {
        setActiveTab("admin");
      } else if (path.startsWith("/results") || hash.startsWith("#results")) {
        const queryParams = new URLSearchParams(search || hash.split("?")[1] || "");
        const q = queryParams.get("search_query") || queryParams.get("q") || "";
        if (q) {
          setSearchQuery(q);
        }
        setActiveTab("search");
      } else if (path.startsWith("/channel/") || hash.startsWith("#channel/")) {
        const rawSlug = (path.replace("/channel/", "") || hash.replace("#channel/", "")).split("?")[0].split("/")[0].trim();
        const cleanHandle = decodeURIComponent(rawSlug).replace(/^@+/, "").trim();
        if (cleanHandle) {
          setSelectedCreator(cleanHandle);
          setActiveTab("channel");
          if (path.includes("/channel/@@")) {
            try {
              window.history.replaceState(null, "", `/channel/@${cleanHandle}`);
            } catch {}
          }
        }
      } else if (path.startsWith("/clip/") || hash.startsWith("#clip/")) {
        const rawClipSlug = (path.replace("/clip/", "") || hash.replace("#clip/", "")).split("?")[0].split("/")[0].trim();
        if (rawClipSlug) {
          setSelectedClipId(rawClipSlug);
          setActiveTab("clip");
        }
      } else if (path.startsWith("/live/join/") || hash.startsWith("#live/join/")) {
        const rawToken = (path.replace("/live/join/", "") || hash.replace("#live/join/", "")).split("?")[0].split("/")[0].trim();
        if (rawToken) {
          setGuestToken(rawToken);
          setActiveTab("live_join");
        }
      } else if (path === "/studio/live" || hash === "#studio/live" || path.startsWith("/studio/live") || hash.startsWith("#studio/live")) {
        setActiveTab("live_studio");
      } else if (path === "/studio" || hash === "#studio") {
        setActiveTab("studio");
      } else if (path.startsWith("/watch/") || hash.startsWith("#watch/")) {
        const rawId = (path.replace("/watch/", "") || hash.replace("#watch/", "")).split("?")[0].split("/")[0].trim();
        if (rawId) {
          const lowerId = rawId.toLowerCase();
          const cleanSlug = lowerId.replace(/^stream_/, "").replace(/_official$/, "").replace(/[-_]/g, " ").trim();

          const match = (videos || SEED_VIDEOS).find((v) => {
            if (v.streamMediaId === rawId || v.id === rawId || v.uid === rawId) return true;
            if (cleanSlug && v.title && v.title.toLowerCase().trim() === cleanSlug) return true;
            return false;
          });

          if (match) {
            setSelectedVideo(match);
          } else {
            // Authentic fallbacks for canonical tracks
            if (lowerId.includes("love_tonight") || lowerId.includes("love-tonight") || cleanSlug === "love tonight") {
              setSelectedVideo(LOVE_TONIGHT_VIDEO);
            } else if (lowerId.includes("sweet_margaret") || lowerId.includes("sweet-margaret") || cleanSlug === "sweet margaret") {
              setSelectedVideo(SWEET_MARGARET_VIDEO);
            } else if (lowerId.includes("margaret_my_love") || lowerId.includes("margaret-my-love") || cleanSlug === "margaret my love") {
              setSelectedVideo(MARGARET_MY_LOVE_VIDEO);
            } else {
              setSelectedVideo({
                id: rawId,
                uid: rawId,
                streamMediaId: rawId,
                title: `Stream Video ${rawId.substring(0, 8)}`,
                creatorName: "HP - Helix Creator",
                creatorHandle: "@helixcreator",
                viewsCount: "1 view",
                uploadDate: "Recently",
                duration: "3:45",
                thumbnailUrl: `https://videodelivery.net/${rawId}/thumbnails/thumbnail.jpg?time=1s&height=360`,
                hlsManifestUrl: `https://videodelivery.net/${rawId}/manifest/video.m3u8`,
                category: "Music",
                description: "",
              });
            }

            // Dynamically query API for exact video record
            fetchVideoById(rawId).then((live) => {
              if (live) setSelectedVideo(live);
            }).catch(() => {});
          }
          setActiveTab("player");
        }
      } else if (search.includes("v=")) {
        const params = new URLSearchParams(search);
        const vUid = params.get("v");
        if (vUid) {
          const match = (videos || SEED_VIDEOS).find((v) => v.streamMediaId === vUid || v.id === vUid);
          if (match) {
            setSelectedVideo(match);
            setActiveTab("player");
          }
        }
      }
    };

    handleUrlRouting();
    window.addEventListener("popstate", handleUrlRouting);
    return () => window.removeEventListener("popstate", handleUrlRouting);
  }, [videos]);

  // Real-time dynamic filtering by Category + Search Query
  const filteredVideos = useMemo(() => {
    const sourceList = videos && videos.length > 0 ? videos : SEED_VIDEOS;
    const catLower = selectedCategory.toLowerCase();
    const queryLower = searchQuery.trim().toLowerCase();

    return sourceList.filter((video) => {
      const matchesCategory =
        catLower === "all" ||
        (video.category || "").toLowerCase() === catLower ||
        (catLower === "cinema" && (video.category || "").toLowerCase() === "african cinema");

      // 2. Real-time Multi-Field Search Query Filtering
      let matchesSearch = true;
      if (queryLower) {
        const titleMatch = video.title.toLowerCase().includes(queryLower);
        const creatorMatch = video.creatorName.toLowerCase().includes(queryLower);
        const handleMatch = video.creatorHandle.toLowerCase().includes(queryLower);
        const uidMatch = video.streamMediaId?.toLowerCase().includes(queryLower);
        const descMatch = video.description?.toLowerCase().includes(queryLower);
        const catMatch = video.category?.toLowerCase().includes(queryLower);

        matchesSearch = titleMatch || creatorMatch || handleMatch || uidMatch || descMatch || catMatch;
      }

      return matchesCategory && matchesSearch;
    });
  }, [videos, selectedCategory, searchQuery]);

  // Algorithmic trending ranking by views count / velocity
  const trendingVideos = useMemo(() => {
    const list = [...(videos && videos.length > 0 ? videos : SEED_VIDEOS)];
    return list.sort((a, b) => {
      const parseCount = (s?: string | number) => {
        if (typeof s === "number") return s;
        if (!s) return 0;
        const num = parseFloat(s);
        if (typeof s === "string" && (s.includes("K") || s.includes("k"))) return num * 1000;
        if (typeof s === "string" && (s.includes("M") || s.includes("m"))) return num * 1000000;
        return num || 0;
      };
      return parseCount(b.viewsCount || b.views) - parseCount(a.viewsCount || a.views);
    });
  }, [videos]);

  // Channel subscriptions filter
  const subscriptionVideos = useMemo(() => {
    const sourceList = videos && videos.length > 0 ? videos : SEED_VIDEOS;
    if (typeof localStorage === "undefined") return sourceList;

    const subs = sourceList.filter((v) => {
      const handle = (v.creatorHandle || "").replace(/^@/, "").toLowerCase();
      const name = (v.creatorName || "").toLowerCase().replace(/\s+/g, "");
      return (
        localStorage.getItem(`helix_sub_${handle}`) === "true" ||
        localStorage.getItem(`helix_sub_${name}`) === "true"
      );
    });

    return subs;
  }, [videos]);

  const handleVideoSelect = (video: VideoItem, playlist?: Playlist) => {
    if (playerCtx) {
      playerCtx.setMiniplayerVisible(false);
      playerCtx.playVideo(video, true);
    }
    setSelectedVideo(video);
    setSelectedPlaylist(playlist);
    setActiveTab("player");
    const targetUid = video.streamMediaId || video.uid || video.id;
    if (typeof window !== "undefined" && window.history?.pushState) {
      window.history.pushState(null, "", `/watch/${targetUid}`);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCreatorSelect = (creatorHandleOrName: string) => {
    if (activeTab === "player" && playerCtx && selectedVideo) {
      playerCtx.setMiniplayerVisible(true);
    }
    const clean = (creatorHandleOrName || "").replace(/^@+/, "").trim();
    setSelectedCreator(clean);
    setActiveTab("channel");
    try {
      window.history.pushState(null, "", `/channel/@${clean}`);
    } catch {}
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTabChange = (tab: NavTab) => {
    if (activeTab === "player" && tab !== "player" && playerCtx && selectedVideo) {
      playerCtx.setMiniplayerVisible(true);
    }
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    if (typeof window !== "undefined" && window.history?.pushState) {
      if (tab === "home") window.history.pushState(null, "", "/");
      else if (tab === "shorts") window.history.pushState(null, "", "/shorts");
      else if (tab === "trending") window.history.pushState(null, "", "/trending");
      else if (tab === "history") window.history.pushState(null, "", "/history");
      else if (tab === "subscriptions") window.history.pushState(null, "", "/subscriptions");
      else if (tab === "offline") window.history.pushState(null, "", "/offline");
      else if (tab === "settings") window.history.pushState(null, "", "/settings");
    }
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All");
  };

  const handleEditVideo = (video: VideoItem) => {
    setEditingVideo(video);
  };

  const handleVideoEdited = () => {
    setEditingVideo(null);
    refetch();
  };

  const handleDeleteVideo = async (video: VideoItem) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${video.title}" from HP - Helix?`);
    if (!confirmed) return;

    const targetUid = video.streamMediaId || video.id;
    const ok = await deleteVideo(targetUid);
    if (ok) {
      refetch();
    } else {
      alert("Failed to delete video. Please try again.");
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;

    // 1. Direct HLS or Cloudflare full URL input
    if (query.startsWith("http://") || query.startsWith("https://")) {
      const match = query.match(/(?:videodelivery\.net|cloudflarestream\.com)\/([a-zA-Z0-9_-]+)/);
      const uid = match ? match[1] : (query.match(/\/([a-zA-Z0-9_-]+)\.m3u8/)?.[1] || `stream_${Date.now()}`);
      setSelectedVideo({
        id: `custom_${Date.now()}`,
        title: query.includes(".m3u8") ? `HLS Stream: ${uid}` : `Cloudflare Stream (${uid})`,
        creatorName: "HP - Helix Streaming Engine",
        creatorHandle: "@helixcreator",
        viewsCount: "1 view",
        uploadDate: "Just now",
        duration: "--:--",
        thumbnailUrl: `https://videodelivery.net/${uid}/thumbnails/thumbnail.jpg?time=1s&height=360`,
        streamMediaId: uid,
        hlsManifestUrl: query,
        category: "Tech",
        description: `Direct HLS manifest loaded from URL: ${query}`,
      });
      setActiveTab("player");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // 2. Direct Cloudflare Stream UID (hex or alphanumeric identifier)
    const isCloudflareUid = /^[a-fA-F0-9]{32}$/.test(query) || (query.length >= 8 && !query.includes(" ") && !query.includes("."));
    if (isCloudflareUid) {
      const existing = (videos || SEED_VIDEOS).find(
        (v) => v.streamMediaId?.toLowerCase() === query.toLowerCase() || v.id?.toLowerCase() === query.toLowerCase()
      );

      if (existing) {
        setSelectedVideo(existing);
      } else {
        setSelectedVideo({
          id: `cf_${query}`,
          title: `Cloudflare Stream UID: ${query.substring(0, 12)}...`,
          creatorName: "Cloudflare Stream Media",
          creatorHandle: "@cloudflarestream",
          viewsCount: "1 view",
          uploadDate: "Just now",
          duration: "--:--",
          thumbnailUrl: `https://videodelivery.net/${query}/thumbnails/thumbnail.jpg?time=1s&height=360`,
          streamMediaId: query,
          hlsManifestUrl: `https://videodelivery.net/${query}/manifest/video.m3u8`,
          category: "Tech",
          description: `Direct Cloudflare Stream UID: ${query}`,
        });
      }
      setActiveTab("player");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // 3. Search Results Route & View
    setActiveTab("search");
    try {
      window.history.pushState(null, "", `/results?search_query=${encodeURIComponent(query)}`);
    } catch {}
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Under-Construction Maintenance Gatekeeper with developer passkey bypass
  const isExemptPath =
    typeof window !== "undefined" &&
    (window.location.pathname === "/health" ||
      window.location.pathname === "/_health" ||
      window.location.pathname.startsWith("/api/") ||
      window.location.pathname.includes("webhook"));

  if (isLocked && !isExemptPath) {
    return <UnderConstruction onUnlock={() => setIsLocked(false)} />;
  }

  if (activeTab === "live_studio") {
    return (
      <Suspense fallback={<ViewLoadingFallback />}>
        <LiveStudio
          onExit={() => {
            setActiveTab("studio");
            try {
              window.history.pushState({}, "", "/studio");
            } catch {}
          }}
        />
      </Suspense>
    );
  }

  if (activeTab === "live_join") {
    return (
      <Suspense fallback={<ViewLoadingFallback />}>
        <GuestJoinView
          token={guestToken}
          onNavigateHome={() => {
            setActiveTab("home");
            try {
              window.history.pushState({}, "", "/");
            } catch {}
          }}
        />
      </Suspense>
    );
  }

  if (activeTab === "studio") {
    return (
      <Suspense fallback={<ViewLoadingFallback />}>
        <StudioLayout
          videos={videos && videos.length > 0 ? videos : SEED_VIDEOS}
          onExitStudio={() => {
            setActiveTab("home");
            try {
              window.history.pushState({}, "", "/");
            } catch {}
          }}
          onViewChannel={(handle) => {
            const clean = (handle || "").replace(/^@+/, "").trim();
            setSelectedCreator(clean);
            setActiveTab("channel");
            try {
              window.history.pushState({}, "", `/channel/@${clean}`);
            } catch {}
          }}
          onSelectVideo={handleVideoSelect}
          onEditVideo={handleEditVideo}
          onDeleteVideo={handleDeleteVideo}
        />
        {/* YouTube-Style Create Channel Onboarding Modal */}
        <CreateChannelModal
          isOpen={isCreateChannelModalOpen}
          onClose={() => setIsCreateChannelModalOpen(false)}
          onSuccess={(newChannel) => {
            const clean = (newChannel.handle || "").replace(/^@+/, "").trim();
            setSelectedCreator(clean);
            setActiveTab("channel");
            try {
              window.history.pushState({}, "", `/channel/@${clean}`);
            } catch {}
          }}
        />
        {/* Persistent Floating Miniplayer during Studio Management */}
        <MiniPlayer onExpand={() => { setActiveTab("player"); }} />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex flex-col font-sans selection:bg-emerald-500 selection:text-zinc-950">
      {/* 1. Header Navigation Bar with Real-time Autocomplete */}
      <Header
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearchSubmit={handleSearchSubmit}
        onToggleSidebar={handleToggleSidebar}
        videos={videos && videos.length > 0 ? videos : SEED_VIDEOS}
        onSelectVideo={handleVideoSelect}
        onSelectCreator={handleCreatorSelect}
      />

      {/* 2. Collapsible Left Sidebar & Mobile Overlay Drawer */}
      <Sidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isMobileOpen={isMobileMenuOpen}
        onCloseMobile={() => setIsMobileMenuOpen(false)}
      />

      {/* 3. Main Body Container with dynamic sidebar margin */}
      <div
        className={`w-full max-w-full overflow-x-hidden min-w-0 flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? "md:pl-16" : "pl-0 md:pl-60"
        }`}
      >
        <main className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 md:pb-6 space-y-4 sm:space-y-6 min-w-0">
          {/* PWA App Install & Push Notification Banner */}
          <PwaInstallBanner />
          {/* Offline & Low-Bandwidth Network Toast */}
          <NetworkBanner onNavigateOffline={() => setActiveTab("offline")} />

          {/* Category Pill Bar & Quick Action Strip (Visible on Home Feed) */}
          {(activeTab === "home" || activeTab === "trending" || activeTab === "subscriptions") && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 sm:gap-4 w-full min-w-0">
                <div className="flex-1 min-w-0 overflow-hidden">
                  <CategoryBar
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                  />
                </div>
                <button
                  onClick={() => refetch()}
                  className="p-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-400 hover:text-white transition-all shrink-0"
                  title="Refresh Videos Feed"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin text-emerald-400" : ""}`} />
                </button>
              </div>

              {/* Active Filter Chips Bar if search query or category is active */}
              {(selectedCategory !== "All" || searchQuery.trim()) && (
                <div className="flex items-center gap-2 text-xs text-zinc-400 font-medium">
                  <span>Active Filters:</span>
                  {selectedCategory !== "All" && (
                    <span className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-800 text-emerald-400 px-2.5 py-0.5 rounded-full text-[11px]">
                      Category: {selectedCategory}
                      <button
                        onClick={() => setSelectedCategory("All")}
                        className="hover:text-white ml-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {searchQuery.trim() && (
                    <span className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-800 text-emerald-400 px-2.5 py-0.5 rounded-full text-[11px]">
                      Search: &ldquo;{searchQuery}&rdquo;
                      <button
                        onClick={() => setSearchQuery("")}
                        className="hover:text-white ml-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  <button
                    onClick={handleResetFilters}
                    className="text-[11px] text-zinc-500 hover:text-zinc-300 underline ml-2"
                  >
                    Reset all
                  </button>
                </div>
              )}
            </div>
          )}

          {/* HOME FEED GRID */}
          {activeTab === "home" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white tracking-tight">
                  {selectedCategory === "All" ? "Recommended Videos" : `${selectedCategory} Videos`}
                </h2>
                <span className="text-xs text-zinc-500 font-mono">
                  {isLoading ? "Loading stream collection..." : `${filteredVideos.length} videos available`}
                </span>
              </div>

              <VideoGrid
                videos={filteredVideos}
                onVideoSelect={handleVideoSelect}
                isLoading={isLoading}
                onResetFilters={handleResetFilters}
                onEditVideo={handleEditVideo}
                onDeleteVideo={handleDeleteVideo}
                onCreatorClick={handleCreatorSelect}
              />
            </div>
          )}

          <ErrorBoundary componentName="AppTabs">
            <Suspense fallback={<ViewLoadingFallback />}>
            {/* SEARCH RESULTS TAB */}
            {activeTab === "search" && (
              <SearchResults
                query={searchQuery}
                videos={videos && videos.length > 0 ? videos : SEED_VIDEOS}
                onVideoSelect={handleVideoSelect}
                onResetSearch={handleResetFilters}
                onCreatorClick={handleCreatorSelect}
              />
            )}

            {/* SHORTS / REELS VERTICAL FEED */}
            {activeTab === "shorts" && (
              <Shorts
                onNavigateWatch={(vidId, time) => {
                  const match = (videos || SEED_VIDEOS).find((v) => v.id === vidId || v.streamMediaId === vidId);
                  if (match) {
                    setSelectedVideo(match);
                  } else {
                    fetchVideoById(vidId).then((live) => {
                      if (live) setSelectedVideo(live);
                    }).catch(() => {});
                  }
                  setActiveTab("player");
                  try {
                    window.history.pushState(null, "", `/watch/${vidId}${time ? `?t=${time}` : ""}`);
                  } catch {}
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onNavigateChannel={handleCreatorSelect}
              />
            )}

            {/* TRENDING TAB */}
            {activeTab === "trending" && (
              <Trending
                videos={trendingVideos}
                onVideoSelect={handleVideoSelect}
                onCreatorClick={handleCreatorSelect}
              />
            )}

            {/* SUBSCRIPTIONS TAB */}
            {activeTab === "subscriptions" && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                  <Tv2 className="w-5 h-5 text-emerald-400" />
                  <h2 className="text-lg font-bold text-white tracking-tight">{t.nav.subscriptions}</h2>
                </div>
                {subscriptionVideos.length > 0 ? (
                  <VideoGrid
                    videos={subscriptionVideos}
                    onVideoSelect={handleVideoSelect}
                    isLoading={isLoading}
                    onResetFilters={handleResetFilters}
                    onEditVideo={handleEditVideo}
                    onDeleteVideo={handleDeleteVideo}
                    onCreatorClick={handleCreatorSelect}
                  />
                ) : (
                  <div className="py-20 text-center space-y-4 bg-zinc-950/80 border border-zinc-800/80 rounded-3xl p-8 max-w-xl mx-auto shadow-xl">
                    <div className="w-16 h-16 rounded-3xl bg-zinc-900 border border-zinc-800 text-zinc-500 flex items-center justify-center mx-auto text-lg">
                      <Tv2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-base font-bold text-white">No channel subscriptions yet</h3>
                      <p className="text-xs text-zinc-400 max-w-sm mx-auto">
                        Subscribe to official channels like <span className="text-emerald-400 font-bold">@helixcreator</span> or <span className="text-emerald-400 font-bold">@gospelhub</span> to view their latest uploads here.
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveTab("home")}
                      className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold rounded-xl shadow-lg shadow-emerald-500/20 active:scale-95 transition-all cursor-pointer"
                    >
                      Explore Videos
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === "history" && (
              <HistoryPage
                videos={videos && videos.length > 0 ? videos : SEED_VIDEOS}
                onVideoSelect={handleVideoSelect}
                onExploreVideos={() => setActiveTab("home")}
              />
            )}

            {/* CREATOR CHANNEL PAGE */}
            {activeTab === "channel" && (
              <ChannelPage
                creatorHandleOrName={selectedCreator}
                videos={videos && videos.length > 0 ? videos : SEED_VIDEOS}
                onVideoSelect={handleVideoSelect}
                onBack={() => {
                  try {
                    window.history.pushState(null, "", "/");
                  } catch {}
                  setActiveTab("home");
                }}
                onEditVideo={handleEditVideo}
                onDeleteVideo={handleDeleteVideo}
                onUploadVideo={() => setActiveTab("upload")}
              />
            )}

            {/* WATCH VIEW (Player + Dynamic Up Next Column) */}
            {activeTab === "player" && (
              <WatchView
                video={selectedVideo}
                playlist={selectedPlaylist}
                relatedVideos={videos}
                onSelectRelatedVideo={setSelectedVideo}
                onCreatorClick={handleCreatorSelect}
              />
            )}

            {/* UPLOAD STUDIO */}
            {activeTab === "upload" && (
              <VideoUploadModal
                isOpen={true}
                channelId={channel?.id || "chn_helixcreator"}
                channelName={channel?.channel_name || "HP - Helix Creator"}
                channelHandle={channel?.handle || "@helixcreator"}
                onClose={() => setActiveTab("studio")}
                onSuccess={() => {
                  refetch();
                  setActiveTab("studio");
                }}
              />
            )}

            {/* OFFLINE DOWNLOADS LIBRARY */}
            {activeTab === "offline" && (
              <OfflineLibrary
                onVideoSelect={handleVideoSelect}
                onExploreVideos={() => setActiveTab("home")}
              />
            )}

            {/* ARCHITECTURE VIEW */}
            {activeTab === "arch" && <ArchitectureView />}

            {/* SETTINGS VIEW */}
            {activeTab === "settings" && <SettingsPage />}

            {/* GLOBAL PLATFORM ADMIN DASHBOARD PROTECTED BY RBAC ADMIN ROUTE */}
            {activeTab === "admin" && (
              <AdminRoute
                onRedirectHome={() => {
                  setActiveTab("home");
                  try {
                    window.history.pushState(null, "", "/");
                  } catch {}
                }}
              >
                <AdminDashboard />
              </AdminRoute>
            )}

            {/* DEDICATED LOOPING CLIP VIEW */}
            {activeTab === "clip" && (
              <ClipView
                clipId={selectedClipId}
                onNavigateHome={() => {
                  setActiveTab("home");
                  try {
                    window.history.pushState(null, "", "/");
                  } catch {}
                }}
                onNavigateToWatch={(videoId, timestamp) => {
                  const targetVideo = (videos || SEED_VIDEOS).find(
                    (v) => v.id === videoId || v.streamMediaId === videoId || v.uid === videoId
                  );
                  if (targetVideo) {
                    setSelectedVideo(targetVideo);
                  }
                  setActiveTab("player");
                  try {
                    const timeQuery = timestamp ? `?t=${timestamp}` : "";
                    window.history.pushState({}, "", `/watch/${videoId}${timeQuery}`);
                  } catch {}
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onNavigateToChannel={(handle) => {
                  const clean = (handle || "").replace(/^@+/, "").trim();
                  setSelectedCreator(clean);
                  setActiveTab("channel");
                  try {
                    window.history.pushState({}, "", `/channel/@${clean}`);
                  } catch {}
                }}
              />
            )}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <Suspense fallback={null}>
        {/* Edit Video Modal */}
        <EditVideoModal
          isOpen={!!editingVideo}
          video={editingVideo}
          onClose={() => setEditingVideo(null)}
          onSuccess={handleVideoEdited}
        />

        {/* GitHub Authentication Modal */}
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        />

        {/* YouTube-Style Create Channel Onboarding Modal */}
        <CreateChannelModal
          isOpen={isCreateChannelModalOpen}
          onClose={() => setIsCreateChannelModalOpen(false)}
          onSuccess={(newChannel) => {
            const clean = (newChannel.handle || "").replace(/^@+/, "").trim();
            setSelectedCreator(clean);
            setActiveTab("channel");
            try {
              window.history.pushState({}, "", `/channel/@${clean}`);
            } catch {}
          }}
        />
      </Suspense>

      {/* Persistent Floating Miniplayer (SPA Navigation Continuity) */}
      <MiniPlayer onExpand={() => { setActiveTab("player"); }} />

      {/* Global Production Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800/80 py-6 text-xs text-zinc-500 font-sans mt-auto select-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-zinc-400">
            <span>© 2026 HP - Helix · High-Performance Streaming</span>
          </div>

          <div className="flex items-center gap-4 text-[11px] font-mono text-zinc-500">
            <span>EN | FR | LN | SW</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
